import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Building2, User } from "lucide-react";
import SEO from "@/components/SEO";
import { MaskLines, Reveal } from "@/components/Reveal";
import { Section, SectionHead, NumberedList } from "@/components/sections";
import { CTALink, SectionLabel, Stat } from "@/components/ui-kit";
import Marquee from "@/components/Marquee";

const BUILDING = "https://images.unsplash.com/photo-1490351267196-b7a67e26e41b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGZhY2FkZSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4NjI3Mzk4NHww&ixlib=rb-4.1.0&q=85";

const manifesto = [
  { title: "Plan first, product later", body: "We start with your goals, cash flows and constraints — not a product pitch. The plan decides the products, never the other way round." },
  { title: "One advisor, the full picture", body: "Businesses and families both deserve a single relationship that sees loans, investments, insurance and succession as one connected balance sheet." },
  { title: "Reviews, not abandonment", body: "Markets move and life changes. We schedule structured reviews so your plan is maintained through cycles — not sold once and forgotten." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div>
      <SEO
        path="/"
        title="Financial & Wealth Solutions"
        description="Rao Investment Solutions — an AMFI-registered Mutual Fund Distributor. Financial solutions for businesses, wealth solutions for individuals. Plan-first, one-advisor advice by Karthik Rao."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-24">
        <motion.div style={{ y, opacity }} className="max-w-[1400px] w-full mx-auto px-6 md:px-12 relative z-10">
          <Reveal>
            <SectionLabel>AMFI-registered Mutual Fund Distributor</SectionLabel>
          </Reveal>
          <h1 className="mt-6 font-serif font-medium text-navy tracking-tight text-[13vw] leading-[0.95] sm:text-7xl lg:text-8xl">
            <MaskLines lines={["Financial Solutions", "for Businesses."]} delay={0.15} />
            <span className="block text-orange">
              <MaskLines lines={["Wealth Solutions", "for Individuals."]} delay={0.45} />
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 font-sans text-lg text-slate max-w-xl"
          >
            One relationship for the whole balance sheet — from working capital and corporate bonds to
            mutual funds, PMS and retirement. Advice that plans first and sells never.
          </motion.p>
        </motion.div>

        {/* Dual path split */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="max-w-[1400px] w-full mx-auto px-6 md:px-12 mt-14 relative z-10"
        >
          <div className="grid md:grid-cols-2 border border-navy/15">
            <PathCard
              to="/business"
              icon={Building2}
              label="For Businesses"
              title="Financial Solutions"
              points="Loans · Working capital · High-yield bonds · Corporate insurance"
              testid="path-business"
            />
            <PathCard
              to="/individual"
              icon={User}
              label="For Individuals"
              title="Wealth Solutions"
              points="Mutual funds · PMS & AIF · Direct equity · Insurance"
              dark
              testid="path-individual"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats strip */}
      <Section className="!py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <Stat value="150+" label="Investors served" />
          <Stat value="5" label="Enterprise businesses" />
          <Stat value="₹50 Cr+" label="Assets advised" />
          <Stat value="1" label="Advisor, your full picture" />
        </div>
      </Section>

      {/* Manifesto */}
      <Section cream>
        <SectionHead label="Our philosophy" title="Three principles we don't compromise on" />
        <NumberedList items={manifesto} />
      </Section>

      {/* Founder spotlight */}
      <section className="bg-navy text-white relative z-10 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-4">
            <div className="border-l-2 border-citrine pl-6">
              <div className="font-serif text-3xl md:text-4xl text-white">Karthik Rao</div>
              <div className="mt-2 font-sans text-xs uppercase tracking-[0.18em] text-white/50">Founder</div>
              <div className="mt-4 font-data text-sm text-white/60 leading-relaxed">
                Wealth Manager &amp; Broker Associate<br />MBA, SIMSREE
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-8 md:pl-8">
            <SectionHead label="The person behind the plan" title="Institutional pedigree, human advice" dark />
            <div className="space-y-5 font-sans text-white/70 text-lg leading-relaxed">
              <Reveal><p>Karthik Rao holds an MBA from SIMSREE and a B.Com in Financial Markets, with 9+ years across financial services — including experience across Sharekhan, Motilal Oswal, Angel One and Bonds Bazaar.</p></Reveal>
              <Reveal delay={0.08}><p>That mix of distribution, analytics and markets shapes how Rao Investment Solutions works: rigorous on process, plain-spoken on advice, and focused on outcomes you can actually live with.</p></Reveal>
            </div>
            <Reveal delay={0.16}>
              <div className="mt-8">
                <CTALink to="/about" variant="secondaryLight" data-testid="home-about-cta">Read Karthik's approach</CTALink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Business visual band */}
      <section className="relative z-10">
        <div className="grid md:grid-cols-2">
          <div className="relative min-h-[320px]">
            <img src={BUILDING} alt="Corporate finance" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="bg-cream p-10 md:p-16 flex flex-col justify-center">
            <SectionLabel>Two paths, one desk</SectionLabel>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl text-navy tracking-tight">Whether you run a company or a household, the questions rhyme.</h2>
            <p className="mt-5 font-sans text-slate text-lg leading-relaxed">Fund growth without over-leverage. Protect against shocks. Put idle capital to work. We answer them with the same discipline — sized to you.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <CTALink to="/business" variant="secondary" data-testid="band-business-cta">Explore Business</CTALink>
              <CTALink to="/individual" variant="primary" data-testid="band-individual-cta">Explore Individual</CTALink>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Final CTA */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto">
          <SectionHead label="Let's begin" title="A conversation costs nothing. Not having a plan can cost plenty." dark center />
          <div className="flex justify-center">
            <CTALink to="/contact" variant="primary" data-testid="home-final-cta">Talk to us</CTALink>
          </div>
        </div>
      </Section>
    </div>
  );
}

const PathCard = ({ to, icon: Icon, label, title, points, dark = false, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group relative p-10 md:p-12 transition-colors duration-300 ${
      dark ? "bg-navy text-white hover:bg-navy-deep" : "bg-cream hover:bg-white border-b md:border-b-0 md:border-r border-navy/15"
    }`}
  >
    <div className="flex items-center justify-between">
      <Icon className={`w-8 h-8 ${dark ? "text-citrine" : "text-navy"}`} strokeWidth={1.25} />
      <ArrowUpRight className={`w-6 h-6 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${dark ? "text-white/60" : "text-slate"}`} strokeWidth={1.5} />
    </div>
    <div className={`mt-8 font-sans text-xs uppercase tracking-[0.24em] ${dark ? "text-citrine" : "text-slate"}`}>{label}</div>
    <h3 className={`mt-2 font-serif text-3xl md:text-4xl ${dark ? "text-white" : "text-navy"}`}>{title}</h3>
    <p className={`mt-4 font-data text-xs md:text-sm ${dark ? "text-white/50" : "text-slate"}`}>{points}</p>
  </Link>
);
